import {
  type AuthServerDependencies,
  getFullUserEffect,
  liveAuthServerDependencies,
} from "@crm/access/auth/server-core";
import type { User } from "@crm/db/schema/auth";
import { serverEffectRuntime } from "@crm/effect/server/runtime";
import { createError } from "@crm/errors";
import type { RequestMiddlewareAfterServer } from "@tanstack/react-start";
import { createMiddleware } from "@tanstack/react-start";
import { Effect } from "effect";

export type AuthenticatedContext = { user: User; requestId: string };

export const resolveAuthenticatedContextEffect = (
  {
    request,
    requestId,
  }: {
    request: Request;
    requestId: string;
  },
  dependencies?: AuthServerDependencies,
): Effect.Effect<AuthenticatedContext, unknown> =>
  Effect.gen(function* () {
    const liveDependencies = dependencies ?? liveAuthServerDependencies;
    const user = yield* getFullUserEffect(
      {
        headers: request.headers,
        requestId,
        handler: "requireAuth",
      },
      liveDependencies,
    );

    if (!user) {
      return yield* Effect.fail(createError.unauthenticated());
    }

    if (user.status !== "active") {
      return yield* Effect.fail(createError.accountDisabled("账号尚未启用或已被禁用"));
    }

    return { user, requestId };
  });

/**
 * 认证中间件工厂函数
 *
 * 验证用户已登录,并将用户信息注入到 context 中
 *
 * @example
 * ```typescript
 * export const myServerFn = createServerFn({ method: "GET" })
 *   .middleware([requireAuth()])
 *   .handler(async ({ context }) => {
 *     // context.user 现在可用且有类型推断
 *     const { role, staffId } = context.user;
 *   });
 * ```
 */
export function requireAuth(): RequestMiddlewareAfterServer<
  object,
  undefined,
  AuthenticatedContext
> {
  return createMiddleware().server(({ next, request, context }) => {
    const requestId = (context as unknown as { requestId: string }).requestId;

    return serverEffectRuntime
      .runPromise(resolveAuthenticatedContextEffect({ request, requestId }))
      .then((authenticatedContext) =>
        next({
          context: authenticatedContext,
        }),
      );
  });
}
